import {
  EConfiguratorQuestionType,
  EConfiguratorStep,
  IConfiguratorAnswers,
  IConfiguratorFormatContext,
  IConfiguratorStep
} from '../models/configurator.model';
import { getAnswer } from './configurator-answer-path.helper';
import { formatAnswer, getSummaryLabel, truncateSummaryValue } from './configurator-format.helper';
import {
  getQuestionPath,
  getSectionInstances,
  isQuestionAnswered,
  isQuestionVisible,
  resolveText
} from './configurator-rules.helper';

/** The sidebar shows this many answers per step, the rest as "+ n weitere". */
export const SUMMARY_ITEMS_PER_GROUP = 5;

export interface IConfiguratorSummaryItem {
  /** Answer path, unique within the summary. */
  path: string;
  label: string;
  value: string;
}

export interface IConfiguratorSummaryGroup {
  stepIndex: number;
  stepId: EConfiguratorStep;
  current: boolean;
  items: IConfiguratorSummaryItem[];
}

export interface IConfiguratorAnswerItem extends IConfiguratorSummaryItem {
  /** Long text (textarea): shown below its label in the review. */
  long: boolean;
}

export interface IConfiguratorAnswerSection {
  key: string;
  /** Translated section title; empty for untitled sections and repeated ones (their labels carry a prefix). */
  title: string;
  items: IConfiguratorAnswerItem[];
}

/** Answers of the visible, answered questions of a step by section, in the order of the form, not shortened. */
export const getStepAnswerSections = (
  step: IConfiguratorStep,
  context: IConfiguratorFormatContext
): IConfiguratorAnswerSection[] => {
  const answers = context.answers;

  return getSectionInstances(step, answers)
    .map((instance) => {
      const repeated = instance.index !== null;
      const prefix =
        repeated && instance.section.summaryPrefix
          ? instance.section.summaryPrefix(answers, instance.index!, context.translate)
          : '';
      const title = resolveText(instance.section.title, answers);

      const items = instance.section.questions
        .filter((question) => !question.summaryHidden && isQuestionVisible(question, answers, instance.item))
        .map((question) => ({ question, path: getQuestionPath(question, instance) }))
        .filter(({ question, path }) => isQuestionAnswered(question, answers, path))
        .map(({ question, path }) => ({
          path,
          label: `${prefix}${getSummaryLabel(question, context)}`,
          value: formatAnswer(question, getAnswer(answers, path), context),
          long: question.type === EConfiguratorQuestionType.TEXTAREA
        }))
        .filter((item) => item.label && item.value);

      return {
        key: instance.key,
        title:
          title && !prefix
            ? context.translate(title, instance.section.titleParams?.(answers, instance.index))
            : '',
        items
      };
    })
    .filter((section) => section.items.length);
};

/** Answers of the visible, answered questions of a step, in the order of the form. */
export const getStepSummaryItems = (
  step: IConfiguratorStep,
  context: IConfiguratorFormatContext
): IConfiguratorSummaryItem[] =>
  getStepAnswerSections(step, context).reduce<IConfiguratorSummaryItem[]>(
    (items, section) => [
      ...items,
      ...section.items.map(({ path, label, value }) => ({ path, label, value: truncateSummaryValue(value) }))
    ],
    []
  );

export interface IConfiguratorReviewCard {
  stepIndex: number;
  stepId: EConfiguratorStep;
  sections: IConfiguratorAnswerSection[];
}

/** "Prüfen": one card per step before the review, also for steps without answers ("Keine Angaben"). */
export const buildReview = (
  steps: IConfiguratorStep[],
  answers: IConfiguratorAnswers,
  context: Omit<IConfiguratorFormatContext, 'answers'>
): IConfiguratorReviewCard[] =>
  steps
    .map((step, stepIndex) => ({ step, stepIndex }))
    .filter(({ step }) => step.id !== EConfiguratorStep.REVIEW)
    .map(({ step, stepIndex }) => ({
      stepIndex,
      stepId: step.id,
      sections: getStepAnswerSections(step, { ...context, answers })
    }));

/** Steps reached so far that have answers; the review step itself is left out. */
export const buildSummary = (
  steps: IConfiguratorStep[],
  answers: IConfiguratorAnswers,
  currentStep: number,
  maxStep: number,
  context: Omit<IConfiguratorFormatContext, 'answers'>
): IConfiguratorSummaryGroup[] =>
  steps
    .map((step, stepIndex) => ({ step, stepIndex }))
    .filter(({ step, stepIndex }) => stepIndex <= maxStep && step.id !== EConfiguratorStep.REVIEW)
    .map(({ step, stepIndex }) => ({
      stepIndex,
      stepId: step.id,
      current: stepIndex === currentStep,
      items: getStepSummaryItems(step, { ...context, answers })
    }))
    .filter((group) => group.items.length);

export const countSummaryItems = (groups: IConfiguratorSummaryGroup[]): number =>
  groups.reduce((count, group) => count + group.items.length, 0);
